// Tiny LRU cache backed by a Map. Map preserves insertion order, so
// re-inserting on every read effectively moves the entry to the
// most-recently-used end; once `maxSize` is exceeded we evict from
// the front (least-recently-used).
//
// Callers that need a reactive size — the voyage-details store
// surfaces "N details loaded" in the status bar — pass an `onSize`
// callback that fires whenever the cache's size changes.

export interface LruCache<K, V> {
  get(key: K): V | undefined;
  set(key: K, value: V): void;
  has(key: K): boolean;
  delete(key: K): boolean;
  size: () => number;
  clear(): void;
  // Iterate cached values in insertion order (oldest → newest). Does
  // not refresh LRU on read so the caller can scan without churning
  // the eviction order.
  values: () => IterableIterator<V>;
}

export interface LruOptions {
  maxSize: number;
  // Fired after the cache's `size` changes (post-eviction). Not fired
  // when a `set` replaces an existing key without changing size.
  onSize?: (size: number) => void;
}

export function createLruCache<K, V>(opts: LruOptions): LruCache<K, V> {
  if (opts.maxSize <= 0) throw new Error("LruCache: maxSize must be > 0");
  const store = new Map<K, V>();
  let lastSize = 0;
  function notifySize(): void {
    if (store.size === lastSize) return;
    lastSize = store.size;
    opts.onSize?.(lastSize);
  }
  return {
    get(key) {
      const v = store.get(key);
      if (v === undefined) return undefined;
      // Refresh LRU on read — delete + re-insert moves the entry to
      // the most-recently-used end of the iteration order.
      store.delete(key);
      store.set(key, v);
      return v;
    },
    set(key, value) {
      store.delete(key);
      store.set(key, value);
      while (store.size > opts.maxSize) {
        const oldest = store.keys().next();
        if (oldest.done) break;
        store.delete(oldest.value);
      }
      notifySize();
    },
    has(key) {
      return store.has(key);
    },
    delete(key) {
      const had = store.delete(key);
      if (had) notifySize();
      return had;
    },
    size() {
      return store.size;
    },
    clear() {
      store.clear();
      notifySize();
    },
    values() {
      return store.values();
    },
  };
}
